// src/components/recipient/RecipientProposal.tsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play } from "lucide-react";
import confetti from "canvas-confetti";
import { ProposalConfig } from "../../types/recipient.types";
import logo from "../../assets/img/logo.png";

interface RecipientProposalProps {
  config: ProposalConfig & {
    primaryColor?: string;
    secondaryColor?: string;
    archetype?: string;
    theme?: any;
    delayBeforeButtons?: number;
  };
  senderName: string;
  recipientName: string;
  onAccept: () => void;
}

export const RecipientProposal: React.FC<RecipientProposalProps> = ({
  config,
  senderName,
  recipientName,
  onAccept,
}) => {
  const [showButtons, setShowButtons] = useState(false);
  // const [showCameraPrompt, setShowCameraPrompt] = useState(false);
  // const [hasRecorded, setHasRecorded] = useState(false);
  const [isPulsing, setIsPulsing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const theme = useMemo(
    () => ({
      primary: config.primaryColor ?? "#E11D48",
      secondary: config.secondaryColor ?? "#000000",
    }),
    [config.primaryColor, config.secondaryColor]
  );

  // Emotional consent delay - buttons appear after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowButtons(true);
      // Start subtle pulse to draw attention
      setIsPulsing(true);
    }, config.delayBeforeButtons || 3000);

    return () => clearTimeout(timer);
  }, [config.delayBeforeButtons]);

  // Subtle camera prompt appears after buttons (optional)
  // useEffect(() => {
  //   if (showButtons) {
  //     const timer = setTimeout(() => {
  //       setShowCameraPrompt(true);
  //     }, 2000);
  //     return () => clearTimeout(timer);
  //   }
  // }, [showButtons]);

  const handleYes = () => {
    // Ceremonial confetti
    confetti({
      particleCount: 120,
      spread: 100,
      origin: { y: 0.6 },
      colors: [theme.primary, "#ffffff", theme.secondary],
      startVelocity: 25,
      decay: 0.9,
    });

    onAccept();
  };

  // const handleCameraRequest = () => {
  //   setHasRecorded(true);
  //   setShowCameraPrompt(false);
  // };

  console.log("config: ", config);
  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 1.5 } }}
      className="fixed inset-0 z-[1000] text-white overflow-hidden"
    >
      {/* Cinematic background with slow zoom */}
      <motion.div
        initial={{ scale: 1.2 }}
        animate={{ scale: 1 }}
        transition={{ duration: 12, ease: "easeOut" }}
        className="absolute inset-0"
      >
        {config.image ? (
          <div className="absolute inset-0 overflow-hidden">
            <img
              src={config.image}
              alt=""
              className="absolute inset-x-0 top-0 w-full h-full object-cover object-top"
              style={{
                objectPosition: "center",
              }}
            />
          </div>
        ) : (
          <div
            className="w-full h-full"
            style={{
              background: `radial-gradient(circle at center, ${theme.primary}55, ${theme.secondary}dd)`,
            }}
          />
        )}

        {/* Multiple overlay layers for depth */}
        <div className="absolute inset-0 bg-black/40" />
        <div
          className="absolute inset-0"
          style={{
            background: "radial-gradient(circle at center, transparent 20%, black 80%)",
          }}
        />
      </motion.div>

      {/* Breathing glow - emotional heartbeat */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          opacity: [0.1, 0.4, 0.1],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        style={{
          background: `radial-gradient(circle at center, ${theme.primary}44, transparent 70%)`,
        }}
      />

      {/* Main content */}
      <div className="relative z-10 h-full w-full flex flex-col items-center justify-center px-6 text-center">
        {/* Sender attribution - appears first - with text shadow for visibility */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 0.9, y: 0 }}
          transition={{ delay: 0.5, duration: 1 }}
          className="text-[10px] md:text-xs font-light uppercase tracking-[0.3em] mb-6"
          style={{
            color: "white",
            textShadow: "0 2px 4px rgba(0,0,0,0.5), 0 0 10px rgba(0,0,0,0.3)",
          }}
        >
          From{" "}
          <span
            className="font-medium"
            style={{ color: "white", textShadow: "0 2px 4px rgba(0,0,0,0.5)" }}
          >
            {senderName}
          </span>
          <br />
          <br />
          To
        </motion.p>

        {/* Recipient name - with enhanced visibility */}
        <motion.h1
          initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ delay: 1, duration: 1.2 }}
          className="font-serif text-6xl md:text-8xl lg:text-9xl leading-[0.9] font-bold"
          style={{
            color: theme.primary,
            textShadow: `
              0 2px 4px rgba(0,0,0,0.5),
              0 4px 8px rgba(0,0,0,0.3),
              0 8px 16px rgba(0,0,0,0.2)
            `,
            WebkitTextStroke: "1px rgba(0,0,0,0.3)",
          }}
        >
          {recipientName}
        </motion.h1>

        {/* The question - with enhanced visibility */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1.5 }}
          className="mt-8 md:mt-10 text-lg md:text-xl lg:text-2xl max-w-xl font-serif italic leading-relaxed"
          style={{
            color: "white",
            textShadow: "0 2px 4px rgba(0,0,0,0.5), 0 4px 8px rgba(0,0,0,0.3)",
          }}
        >
          Hey {config.question}
        </motion.p>

        {/* FIXED HEIGHT CONTAINER - Prevents layout shift */}
        <div className="relative h-48 w-full max-w-md mt-12 md:mt-16">
          <AnimatePresence mode="wait">
            {/* The waiting moment - before buttons appear */}
            {!showButtons && (
              <motion.div
                key="waiting"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="absolute inset-0 flex flex-col items-center justify-center"
              >
                <motion.div
                  animate={{
                    scale: [1, 1.1, 1],
                    opacity: [0.3, 0.6, 0.3],
                  }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center align-items-center justify-center backdrop-blur-sm"
                  style={{
                    border: `1px solid ${theme.primary}80`,
                    background: `${theme.primary}20`,
                    boxShadow: `0 0 20px ${theme.primary}40`,
                  }}
                >
                  <img src={logo} alt="LRNSY logo" className="w-12 h-12 md:w-18 md:h-18" />
                </motion.div>
                <p
                  className="text-[10px] md:text-xs mt-6 tracking-[0.3em] uppercase font-light"
                  style={{
                    color: "white",
                    textShadow: "0 2px 4px rgba(0,0,0,0.5)",
                  }}
                >
                  Take a moment...
                </p>
              </motion.div>
            )}

            {/* Action buttons - appear after emotional consent */}
            {showButtons && (
              <motion.div
                key="buttons"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0 flex flex-col items-center justify-center"
              >
                {/* Yes button */}
                <motion.button
                  onClick={handleYes}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  animate={
                    isPulsing
                      ? {
                          scale: [1, 1.03, 1],
                        }
                      : {}
                  }
                  transition={{ duration: 2, repeat: isPulsing ? Infinity : 0 }}
                  className="group relative px-10 py-4 md:px-14 md:py-5 rounded-full font-medium overflow-hidden transition-all"
                  style={{
                    background: "rgba(255,255,255,0.1)",
                    backdropFilter: "blur(10px)",
                    border: `1px solid ${theme.primary}60`,
                    color: "#fff",
                    boxShadow: `0 10px 30px -5px ${theme.primary}80, 0 0 20px rgba(0,0,0,0.3)`,
                  }}
                >
                  {/* Button hover effect */}
                  <motion.div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                    style={{
                      background: `linear-gradient(90deg, transparent, ${theme.primary}40, transparent)`,
                    }}
                  />

                  <span className="relative flex items-center justify-center gap-3">
                    <Play
                      size={16}
                      className="opacity-80"
                      style={{
                        color: theme.primary,
                        filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.5))",
                      }}
                    />
                    <span className="text-xs md:text-sm uppercase tracking-[0.2em] font-medium text-white">
                      {config.yesButtonText}
                    </span>
                  </span>
                </motion.button>

                {/* <AnimatePresence>
                  {showCameraPrompt && !hasRecorded && (
                    <motion.button
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 0.7, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      whileHover={{ opacity: 1 }}
                      onClick={handleCameraRequest}
                      className="flex items-center gap-2 transition-colors text-[9px] md:text-xs tracking-wider uppercase mt-6 font-light"
                      style={{
                        color: "white",
                        textShadow: "0 2px 4px rgba(0,0,0,0.5)",
                      }}
                    >
                      <Camera
                        size={12}
                        style={{
                          filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.5))",
                        }}
                      />
                      <span>Capture this moment</span>
                    </motion.button>
                  )}
                </AnimatePresence> */}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Sacred footer - with text shadow */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.3 }}
        transition={{ delay: 3 }}
        className="absolute bottom-8 left-0 right-0 text-center"
      >
        <span
          className="text-[8px] uppercase tracking-[0.5em]"
          style={{
            color: "white",
            textShadow: "0 2px 4px rgba(0,0,0,0.5)",
          }}
        >
          A moment prepared just for you
        </span>
      </motion.div>
    </motion.div>
  );
};
